import momentTz from "moment-timezone";
import { useMarketSchedules } from "./useMarketSchedules";
import { UserScopes } from "../../openapi-typescript/common/user_scopes";
import { MarketSchedules } from "../../openapi-typescript/common/market_schedules";
import { checkIsMarketOpen } from "../../modules/markets/shared/marketSchedulesHelper";

export type DailyTradeHistoryDateFilterResult = {
  filter: Record<string, string> | undefined;
  marketStatus: boolean;
};

export type UseDailyTradeHistoryDateFilterArgs = {
  market: "carbon au" | "carbon int" | "environmental au" | "power au";
  enabled?: boolean;
};

const getPreviousBusinessDayStr = (schedule: MarketSchedules): string => {
  let date = momentTz.tz(schedule.timezone).startOf("day").subtract(1, "day");

  for (let i = 0; i < 60; i++) {
    const dayStr = date.format("YYYY-MM-DD");
    const isHoliday = schedule.calendar?.holidays?.some(
      (h) => h.date === dayStr
    );
    const isWeekend = date.isoWeekday() >= 6;

    if (!isHoliday && !isWeekend) return dayStr;

    date = date.subtract(1, "day");
  }

  return date.format("YYYY-MM-DD");
};

export const useDailyTradeHistoryDateFilter = ({
  market,
  enabled = true,
}: UseDailyTradeHistoryDateFilterArgs): DailyTradeHistoryDateFilterResult => {
  const scopeMap: Record<
    UseDailyTradeHistoryDateFilterArgs["market"],
    UserScopes
  > = {
    "carbon au": "core.notifications.carbon-au-market-status.read",
    "carbon int": "core.notifications.carbon-int-market-status.read",
    "environmental au": "core.notifications.enviro-au-market-status.read",
    "power au": "core.notifications.power-au-market-status.read",
  };

  const scope = scopeMap[market];

  const {
    updatable: marketSchedulesUpdatable,
    loading,
    sync,
  } = useMarketSchedules({
    scope,
    enabled,
  });

  const marketSchedules =
    marketSchedulesUpdatable?.flatMap((u) => (u?.data ? [u.data] : [])) || [];

  const todayStr = momentTz().format("YYYY-MM-DD");
  const schedule = marketSchedules[0];

  const marketStatus = schedule ? checkIsMarketOpen(schedule) : false;
  const prevDayStr = schedule ? getPreviousBusinessDayStr(schedule) : todayStr;

  const dates = `${todayStr},${prevDayStr}`;
  const dateFilter = `in.(${dates})`;

  return {
    filter:
      !loading && sync
        ? { "daily_trade_histories.date": dateFilter }
        : undefined,
    marketStatus,
  };
};
